/**
 * HTML escape helper for print-popup templates.
 *
 * ─── Threat model ─────────────────────────────────────────────────────
 * The print utilities (printTrifoldCard, printRunManifest) build a
 * complete HTML document from template literals and inject it into a
 * popup window via `document.write`. That popup shares this app's origin
 * (cookies, API access). Without escaping, any user-supplied string —
 * card messages, recipient names, sender names, free-text notes — that
 * happens to contain `<script>...</script>` or an `onerror=` attribute
 * would execute when the popup loads. A malicious card message in a PDF
 * upload or manual order entry would be enough to trigger it.
 *
 * ─── Mitigation strategy ──────────────────────────────────────────────
 * Escape every user-data interpolation at the boundary, then `document
 * .write` becomes safe because there is no markup-bearing input left to
 * inject. This is preferred over a full `createElement` rewrite because:
 *   • Both print templates assemble complete HTML pages with print CSS,
 *     page-break rules, and image embeds — rewriting in DOM-construction
 *     style is high-regression-risk for working layout.
 *   • Escaping at the interpolation boundary closes the same hole with
 *     surgical impact.
 *
 * ─── Discipline this requires ─────────────────────────────────────────
 *   1. EVERY user-controlled value that flows into a template literal
 *      MUST be wrapped in `escapeHtml()`. New interpolations are not safe
 *      by default — security here is enforced by reviewer discipline, not
 *      by the framework.
 *   2. This helper handles TEXT-content interpolations only (between
 *      tags, or inside attribute values). It is NOT sufficient if:
 *        • The value goes into a URL attribute (href, src) — see
 *          attribute-context note below.
 *        • The value goes into an inline event handler or `<style>` —
 *          don't do this at all.
 *
 * ─── Attribute-context caveat ─────────────────────────────────────────
 * For URL-bearing attributes (href, src), escapeHtml is necessary but
 * not sufficient: a string like `javascript:alert(1)` would survive
 * entity-escaping (no special chars) and still execute. The print
 * templates today only put SHOP-controlled URLs into href/src (logo
 * fetch URL, signature data URLs), never user-supplied URLs. If that
 * ever changes, add a separate `safeUrl()` helper that rejects
 * non-http(s)/data URLs.
 *
 * ─── Tests ────────────────────────────────────────────────────────────
 * See htmlEscape.test.js — exercises a representative XSS payload set.
 * Run with: `cd /app/frontend && yarn test htmlEscape`
 */
export const escapeHtml = (s) =>
  String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));
